import React, { useState, useEffect, useCallback } from "react";
// Bestem hvilken OS-sektion en indbakke-besked skal navigere til ved klik
function bestemDestination(b) {
 const fra = (b.fra_agent || '').toLowerCase();
 if (fra === 'ide-analyse') return 'ideer';
 if (fra === 'team-noter' || fra === 'team-noter-ai') return 'teamnoter';
 if (fra === 'oekonomi') return 'okonomi';
 if (fra === 'marketing') return 'some';
 if (fra === 'analyse') return 'bi';
 if (fra === 'chief-of-staff' || fra === 'chief_of_staff') return 'chief';
 return 'oversigt'; // deploy, projekt, overvaagning, kode → OS Oversigt med beslutninger
}
const ACCENT = "#3fd4c7";
const AMBER = "#f39c12";
const ROED = "#e74c3c";
const LILLA = "#a78bfa";
const BG = "rgba(8,12,30,0.55)";
const KANT = "1px solid rgba(63,212,199,0.12)";
const PRIORITET_FARVE = {
 lav: "#64748b",
 medium: ACCENT,
 høj: AMBER,
 kritisk: ROED,
};
function formatTid(kl) {
 if (!kl) return "—";
 return new Date(kl).toLocaleString("da-DK", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}
function api(sti, metode = "GET", data) {
 return fetch(`/api/os${sti}`, {
 method: metode,
 credentials: "include",
 headers: { "Content-Type": "application/json" },
 body: data ? JSON.stringify(data) : undefined,
 }).then(r => r.json());
}
const SIDE_STOERRELSE = 20;
// ── INDBAKKE ────────────────────────────────────────────────────
function IndbakkeFane({ onNavigate }) {
 const [beskeder, setBeskeder] = useState([]);
 const [loader, setLoader] = useState(true);
 const [ulæst_filter, setUlæstFilter] = useState(false);
 const [agent_filter, setAgentFilter] = useState("alle");
 const [side, setSide] = useState(0);
 const [bulk_loading, setBulkLoading] = useState(false);
 const [svar_aktiv, setSvarAktiv] = useState(null); // id på besked med åbent svar-felt
 const [svar_tekst, setSvarTekst] = useState({}); // { [id]: tekst }
 const [svar_sender, setSvarSender] = useState({}); // { [id]: bool }
 const hent = useCallback(async () => {
 setLoader(true);
 try {
 const d = await api(`/indbakke${ulæst_filter ? "?ulæst=true" : ""}`);
 setBeskeder(d.beskeder || []);
 } finally {
 setLoader(false);
 }
 }, [ulæst_filter]);
 useEffect(() => { hent(); }, [hent]);
 async function markLæst(id) {
 await api(`/indbakke/${id}/laest`, "PATCH");
 setBeskeder(prev => prev.map(b => b.id === id ? { ...b, laest: true, laest_kl: new Date().toISOString() } : b));
 }
 async function markAlleLæst() {
 const ulæste = beskeder.filter(b => !b.laest);
 if (ulæste.length === 0) return;
 setBulkLoading(true);
 try {
 await Promise.all(ulæste.map(b => api(`/indbakke/${b.id}/laest`, "PATCH")));
 const nu = new Date().toISOString();
 setBeskeder(prev => prev.map(b => b.laest ? b : { ...b, laest: true, laest_kl: nu }));
 } finally {
 setBulkLoading(false);
 }
 }
 async function sendSvar(id) {
 const tekst = (svar_tekst[id] || "").trim();
 if (!tekst) return;
 setSvarSender(prev => ({ ...prev, [id]: true }));
 try {
 await api(`/indbakke/${id}/svar`, "PATCH", { svar: tekst });
 setBeskeder(prev => prev.map(b => b.id === id
 ? { ...b, svar_fra_rene: tekst, svar_kl: new Date().toISOString(), laest: true, laest_kl: b.laest_kl || new Date().toISOString() }
 : b
 ));
 setSvarAktiv(null);
 setSvarTekst(prev => { const n = { ...prev }; delete n[id]; return n; });
 } finally {
 setSvarSender(prev => { const n = { ...prev }; delete n[id]; return n; });
 }
 }
 const ulæste = beskeder.filter(b => !b.laest).length;
 // Unikke agenter til filter
 const agenter = ["alle", ...Array.from(new Set(beskeder.map(b => b.fra_agent).filter(Boolean))).sort()];
 // Filtreret liste
 const filtrerede = beskeder.filter(b => {
 if (ulæst_filter && b.laest) return false;
 if (agent_filter !== "alle" && b.fra_agent !== agent_filter) return false;
 return true;
 });
 const sider = Math.ceil(filtrerede.length / SIDE_STOERRELSE);
 const sideStartIndex = side * SIDE_STOERRELSE;
 const sidens_beskeder = filtrerede.slice(sideStartIndex, sideStartIndex + SIDE_STOERRELSE);
 return (
 <div>
 {/* Toolbar: tæller + bulk-action + filtre + opdater */}
 <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}> <div style={{ fontSize: 13, color: "#7a8fa6", flexShrink: 0 }}>
 {loader ? "Henter…" : `${filtrerede.length} beskeder${ulæste > 0 ? ` · ${ulæste} ulæste` : ""}`}
 </div>
 {/* Bulk: Marker alle som læst */}
 {ulæste > 0 && (
 <button
 onClick={markAlleLæst}
 disabled={bulk_loading}
 style={{
 background: "rgba(63,212,199,0.08)", border: "1px solid rgba(63,212,199,0.25)",
 borderRadius: 8, color: ACCENT, cursor: bulk_loading ? "default" : "pointer",
 fontSize: 12, padding: "5px 12px", fontWeight: 600, opacity: bulk_loading ? 0.6 : 1,
 }}
 >
 {bulk_loading ? "Markerer…" : ` Marker alle ${ulæste} som læst`}
 </button>
 )}
 <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
 {/* OS-12: Agent-filter — min 44px højde for korrekt tap-target på mobil */}
 <select
 value={agent_filter}
 onChange={e => { setAgentFilter(e.target.value); setSide(0); }}
 style={{
 background: "rgba(8,12,30,0.7)", border: "1px solid rgba(63,212,199,0.18)",
 borderRadius: 8, color: "#9db3c8", fontSize: 14, padding: "10px 12px",
 cursor: "pointer", outline: "none", minHeight: 44,
 }}
 >
 {agenter.map(a => (
 <option key={a} value={a} style={{ background: "#0d1525" }}>{a === "alle" ? "Alle agenter" : a}</option>
 ))}
 </select>
 {/* OS-12: Kun ulæste — label med minHeight 44px + større checkbox for touch */}
 <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#9db3c8", cursor: "pointer", minHeight: 44, padding: "0 4px" }}> <input type="checkbox" checked={ulæst_filter} onChange={e => { setUlæstFilter(e.target.checked); setSide(0); }}
 style={{ accentColor: ACCENT, width: 18, height: 18, cursor: "pointer" }} />
 Kun ulæste
 </label> <button onClick={hent} style={{ background: "none", border: KANT, borderRadius: 8, color: ACCENT, cursor: "pointer", fontSize: 12, padding: "5px 12px" }}>
 Opdater
 </button> </div> </div>
 {!loader && filtrerede.length === 0 && (
 <div style={{
 color: "#4a5568", fontSize: 14, padding: "64px 0", textAlign: "center",
 display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
 }}> <span style={{ fontSize: 36 }}></span> <span style={{ color: "#9db3c8", fontWeight: 600 }}>
 {ulæst_filter && agent_filter !== "alle" ? `Ingen ulæste fra ${agent_filter}` :
 ulæst_filter ? "Ingen ulæste beskeder" :
 agent_filter !== "alle" ? `Ingen beskeder fra ${agent_filter}` :
 "Indbakken er tom"}
 </span>
 {(ulæst_filter || agent_filter !== "alle") && (
 <button onClick={() => { setUlæstFilter(false); setAgentFilter("alle"); setSide(0); }}
 style={{ background: "none", border: KANT, borderRadius: 8, color: ACCENT, cursor: "pointer", fontSize: 12, padding: "5px 14px" }}>
 Nulstil filtre
 </button>
 )}
 </div>
 )}
 {sidens_beskeder.map(b => {
 const erKlikbar = !!onNavigate;
 const svarAabent = svar_aktiv === b.id;
 return (
 <div key={b.id}
 style={{
 background: b.laest ? "rgba(8,12,30,0.35)" : BG,
 border: b.laest ? "1px solid rgba(63,212,199,0.07)" : KANT,
 borderRadius: 12, marginBottom: 10,
 borderLeft: `3px solid ${PRIORITET_FARVE[b.prioritet] || ACCENT}`,
 opacity: b.laest ? 0.75 : 1,
 transition: "opacity .15s, background .15s",
 overflow: "hidden",
 }}
 >
 {/* Hoved-kort — klikbart til navigation */}
 <div
 onClick={erKlikbar ? async () => { if (!b.laest) await markLæst(b.id); onNavigate(bestemDestination(b)); } : undefined}
 style={{ padding: "14px 18px", cursor: erKlikbar ? "pointer" : "default" }}
 onMouseEnter={erKlikbar ? e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; } : undefined}
 onMouseLeave={erKlikbar ? e => { e.currentTarget.style.background = "none"; } : undefined}
 > <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}> <div style={{ flex: 1 }}> <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}> <span style={{ fontSize: 14, fontWeight: 700, color: "#e8eaf6" }}>{b.emne}</span> <span style={{ fontSize: 10, fontWeight: 700, color: PRIORITET_FARVE[b.prioritet] || ACCENT,
 background: `${PRIORITET_FARVE[b.prioritet] || ACCENT}22`, borderRadius: 5, padding: "1px 7px", textTransform: "uppercase" }}>
 {b.prioritet}
 </span>
 {!b.laest && (
 <span style={{ fontSize: 10, background: "rgba(63,212,199,0.15)", color: ACCENT, borderRadius: 5, padding: "1px 7px", fontWeight: 700 }}>NY</span>
 )}
 </div> <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>
 Fra: <span style={{ color: "#9db3c8" }}>{b.fra_agent}</span> · {formatTid(b.oprettet_kl)}
 {b.laest && b.laest_kl && <> · Læst {formatTid(b.laest_kl)}</>}
 </div> {/* OS-19: besked er clamp'et til 4 linjer med ellipsis — fuldt indhold ses ved klik */}
 <div style={{
   fontSize: 13, color: "#b0bec5", lineHeight: 1.6,
   display: '-webkit-box',
   WebkitLineClamp: 4,
   WebkitBoxOrient: 'vertical',
   overflow: 'hidden',
   textOverflow: 'ellipsis',
   whiteSpace: 'pre-wrap',
   wordBreak: 'break-word',
 }}>{b.besked}</div> </div> <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
 {!b.laest && (
 <button onClick={e => { e.stopPropagation(); markLæst(b.id); }} style={{
 background: "rgba(63,212,199,0.1)", border: "1px solid rgba(63,212,199,0.3)",
 borderRadius: 8, color: ACCENT, cursor: "pointer", fontSize: 12, padding: "5px 10px", whiteSpace: "nowrap",
 }}> Læst</button>
 )}
 {erKlikbar && (
 <span style={{ fontSize: 11, color: "#4a5568", alignSelf: "center" }}>→</span>
 )}
 </div> </div> </div>
 {/* Eksisterende svar */}
 {b.svar_fra_rene && (
 <div style={{ margin: "0 18px 12px", padding: "10px 14px", background: "rgba(63,212,199,0.06)", borderRadius: 8, borderLeft: "3px solid rgba(63,212,199,0.4)" }}> <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
 Dit svar · {formatTid(b.svar_kl)}
 </div> <div style={{ fontSize: 13, color: "#9db3c8", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{b.svar_fra_rene}</div> </div>
 )}
 {/* Svar-sektion */}
 <div style={{ borderTop: "1px solid rgba(255,255,255,0.04)", padding: "8px 18px 12px" }}>
 {!svarAabent ? (
 <button
 onClick={() => setSvarAktiv(b.id)}
 style={{
 background: "none", border: "1px solid rgba(245,158,11,0.25)",
 borderRadius: 7, color: "#f59e0b", cursor: "pointer",
 fontSize: 12, padding: "4px 12px",
 }}
 >
 {b.svar_fra_rene ? " Opdater svar" : "↩ Svar til CoS"}
 </button>
 ) : (
 <div onClick={e => e.stopPropagation()}> <textarea
 autoFocus
 value={svar_tekst[b.id] || b.svar_fra_rene || ""}
 onChange={e => setSvarTekst(prev => ({ ...prev, [b.id]: e.target.value }))}
 placeholder="Skriv dit svar til Chief of Staff…"
 rows={3}
 style={{
 width: "100%", boxSizing: "border-box",
 background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.3)",
 borderRadius: 8, color: "#e8eaf6", fontSize: 13, padding: "10px 12px",
 fontFamily: "inherit", resize: "vertical", outline: "none", lineHeight: 1.5,
 }}
 /> <div style={{ display: "flex", gap: 8, marginTop: 8 }}> <button
 onClick={() => sendSvar(b.id)}
 disabled={svar_sender[b.id] || !(svar_tekst[b.id] || "").trim()}
 style={{
 background: svar_sender[b.id] ? "rgba(245,158,11,0.3)" : "rgba(245,158,11,0.15)",
 border: "1px solid rgba(245,158,11,0.5)", borderRadius: 8,
 color: "#f59e0b", cursor: svar_sender[b.id] ? "default" : "pointer",
 fontSize: 13, fontWeight: 600, padding: "7px 18px",
 }}
 >
 {svar_sender[b.id] ? "Sender…" : "Send svar til CoS"}
 </button> <button
 onClick={() => { setSvarAktiv(null); setSvarTekst(prev => { const n = { ...prev }; delete n[b.id]; return n; }); }}
 style={{
 background: "none", border: "1px solid rgba(255,255,255,0.1)",
 borderRadius: 8, color: "#64748b", cursor: "pointer", fontSize: 12, padding: "7px 14px",
 }}
 >
 Annuller
 </button> </div> </div>
 )}
 </div> </div>
 );
 })}
 {/* OS-16: Paginering — tydeligere på mobil med større knapper (min 44×44px)
     og synlig side-indikator med pill-styling */}
 {sider > 1 && (
 <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap" }}> <button
 onClick={() => setSide(s => Math.max(0, s - 1))}
 disabled={side === 0}
 style={{
 background: side === 0 ? "none" : "rgba(63,212,199,0.08)",
 border: KANT, borderRadius: 8,
 color: side === 0 ? "#4a5568" : ACCENT,
 cursor: side === 0 ? "default" : "pointer",
 fontSize: 14, padding: "10px 16px",
 minWidth: 44, minHeight: 44,
 opacity: side === 0 ? 0.4 : 1,
 fontWeight: 600,
 }}
 >← Forrige</button>
 <span style={{
   fontSize: 13, color: "#e2e8f0", fontWeight: 700,
   background: "rgba(63,212,199,0.08)",
   border: "1px solid rgba(63,212,199,0.18)",
   borderRadius: 20, padding: "6px 14px",
   minHeight: 44, display: "flex", alignItems: "center",
 }}>
 {side + 1} / {sider}
 <span style={{ fontSize: 11, color: "#7a8fa6", marginLeft: 6 }}>({filtrerede.length} i alt)</span>
 </span> <button
 onClick={() => setSide(s => Math.min(sider - 1, s + 1))}
 disabled={side >= sider - 1}
 style={{
 background: side >= sider - 1 ? "none" : "rgba(63,212,199,0.08)",
 border: KANT, borderRadius: 8,
 color: side >= sider - 1 ? "#4a5568" : ACCENT,
 cursor: side >= sider - 1 ? "default" : "pointer",
 fontSize: 14, padding: "10px 16px",
 minWidth: 44, minHeight: 44,
 opacity: side >= sider - 1 ? 0.4 : 1,
 fontWeight: 600,
 }}
 >Næste →</button> </div>
 )}
 </div>
 );
}
// ── KOMMUNIKATIONSLOG ───────────────────────────────────────────
function KommunikationFane() {
 const [rækker, setRækker] = useState([]);
 const [loader, setLoader] = useState(true);
 useEffect(() => {
 api("/agent-kommunikation?limit=100").then(d => {
 setRækker(d.kommunikation || []);
 setLoader(false);
 });
 }, []);
 return (
 <div> <div style={{ fontSize: 13, color: "#7a8fa6", marginBottom: 16 }}>
 {loader ? "Henter…" : `${rækker.length} beskeder`}
 </div>
 {!loader && rækker.length === 0 && (
 <div style={{ color: "#4a5568", fontSize: 14, padding: "48px 0", textAlign: "center" }}>Ingen agent-kommunikation logget endnu</div>
 )}
 <div style={{ overflowX: "auto" }}> <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}> <thead> <tr style={{ borderBottom: "1px solid rgba(63,212,199,0.15)" }}>
 {["Tidspunkt", "Fra", "Til", "Emne", "Indhold"].map(h => (
 <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
 ))}
 </tr> </thead> <tbody>
 {rækker.map(r => (
 <tr key={r.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
 onMouseEnter={e => e.currentTarget.style.background = "rgba(63,212,199,0.04)"}
 onMouseLeave={e => e.currentTarget.style.background = "none"}> <td style={{ padding: "10px 12px", color: "#64748b", whiteSpace: "nowrap" }}>{formatTid(r.oprettet_kl)}</td> <td style={{ padding: "10px 12px" }}> <span style={{ background: "rgba(63,212,199,0.1)", color: ACCENT, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{r.fra_agent}</span> </td> <td style={{ padding: "10px 12px" }}> <span style={{ background: "rgba(167,139,250,0.1)", color: LILLA, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{r.til_agent}</span> </td> <td style={{ padding: "10px 12px", color: "#c0cfe0", fontWeight: 600 }}>{r.emne}</td> <td style={{ padding: "10px 12px", color: "#7a8fa6", maxWidth: 320 }}> <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }} title={r.indhold}>{r.indhold}</div> </td> </tr>
 ))}
 </tbody> </table> </div> </div>
 );
}
// ── AUTONOME BESLUTNINGER ───────────────────────────────────────
function BeslutningerFane() {
 const [rækker, setRækker] = useState([]);
 const [loader, setLoader] = useState(true);
 const [udvidet, setUdvidet] = useState(null);
 useEffect(() => {
 api("/agent-beslutninger?limit=100").then(d => {
 setRækker(d.beslutninger || []);
 setLoader(false);
 });
 }, []);
 return (
 <div> <div style={{ fontSize: 13, color: "#7a8fa6", marginBottom: 16 }}>
 {loader ? "Henter…" : `${rækker.length} autonome beslutninger logget`}
 </div>
 {!loader && rækker.length === 0 && (
 <div style={{ color: "#4a5568", fontSize: 14, padding: "48px 0", textAlign: "center" }}>Ingen autonome beslutninger logget endnu</div>
 )}
 {rækker.map(r => (
 <div key={r.id} style={{ background: BG, border: KANT, borderRadius: 12, padding: "14px 18px", marginBottom: 10,
 borderLeft: "3px solid rgba(167,139,250,0.5)" }}> <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}> <div style={{ flex: 1 }}> <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}> <span style={{ background: "rgba(167,139,250,0.12)", color: LILLA, borderRadius: 6, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>{r.agent_id}</span> <span style={{ fontSize: 11, color: "#64748b" }}>{formatTid(r.oprettet_kl)}</span> </div> <div style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0", marginBottom: 6 }}>{r.beslutning}</div>
 {r.begrundelse && (
 <div style={{ fontSize: 12, color: "#9db3c8", marginBottom: 4 }}> <span style={{ color: "#64748b" }}>Begrundelse: </span>{r.begrundelse}
 </div>
 )}
 </div>
 {(r.alternativer || r.konsekvens) && (
 <button onClick={() => setUdvidet(udvidet === r.id ? null : r.id)}
 style={{ background: "none", border: KANT, borderRadius: 8, color: "#64748b", cursor: "pointer", fontSize: 11, padding: "4px 10px", flexShrink: 0 }}>
 {udvidet === r.id ? "Skjul" : "Detaljer"}
 </button>
 )}
 </div>
 {udvidet === r.id && (
 <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", gap: 6 }}>
 {r.alternativer && (
 <div style={{ fontSize: 12, color: "#7a8fa6" }}> <span style={{ color: "#64748b", fontWeight: 600 }}>Alternativer: </span>{r.alternativer}
 </div>
 )}
 {r.konsekvens && (
 <div style={{ fontSize: 12, color: "#7a8fa6" }}> <span style={{ color: "#64748b", fontWeight: 600 }}>Konsekvens: </span>{r.konsekvens}
 </div>
 )}
 </div>
 )}
 </div>
 ))}
 </div>
 );
}
// ── HOVED-KOMPONENT ─────────────────────────────────────────────
export default function OSIndbakke({ onNavigate }) {
 const [fane, setFane] = useState("indbakke");
 const faner = [
 { id: "indbakke", label: "Indbakke", farve: ACCENT },
 { id: "kommunikation", label: "Agent-kommunikation", farve: LILLA },
 { id: "beslutninger", label: "Autonome beslutninger", farve: AMBER },
 ];
 return (
 <div> <h1 style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", marginBottom: 6 }}>Indbakke</h1> <p style={{ fontSize: 13, color: "#64748b", marginBottom: 24 }}>
 Notifikationer fra agenter — klik på en besked for at gå til relevant sektion.
 </p>
 {/* Faneblade */}
 <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid rgba(63,212,199,0.1)", paddingBottom: 0 }}>
 {faner.map(f => (
 <button key={f.id} onClick={() => setFane(f.id)} style={{
 padding: "8px 18px", border: "none", background: "none", cursor: "pointer",
 fontSize: 13, fontWeight: fane === f.id ? 700 : 400,
 color: fane === f.id ? f.farve : "#64748b",
 borderBottom: fane === f.id ? `2px solid ${f.farve}` : "2px solid transparent",
 marginBottom: -1, transition: "color .15s",
 }}>
 {f.label}
 </button>
 ))}
 </div>
 {fane === "indbakke" && <IndbakkeFane onNavigate={onNavigate} />}
 {fane === "kommunikation" && <KommunikationFane />}
 {fane === "beslutninger" && <BeslutningerFane />}
 </div>
 );
}